import { Injectable } from '@angular/core';

/**
 * Servicio para gestionar almacenamiento en cookies y localStorage
 * Soporta ambos métodos con una API unificada
 */
@Injectable({
  providedIn: 'root'
})
export class StorageService {

  // Configuración: 'cookie' o 'localStorage'
  private storageType: 'cookie' | 'localStorage' = 'cookie';

  constructor() {}

  /**
   * Cambia el tipo de almacenamiento
   */
  setStorageType(type: 'cookie' | 'localStorage'): void {
    this.storageType = type;
  }

  /**
   * Guarda un valor
   * @param key Clave
   * @param value Valor (ya encriptado)
   * @param days Días de expiración (solo para cookies)
   */
  set(key: string, value: string, days: number = 30): void {
    if (this.storageType === 'cookie') {
      this.setCookie(key, value, days);
    } else {
      this.setLocalStorage(key, value);
    }
  }

  /**
   * Obtiene un valor
   */
  get(key: string): string | null {
    if (this.storageType === 'cookie') {
      return this.getCookie(key);
    } else {
      return this.getLocalStorage(key);
    }
  }

  /**
   * Elimina un valor
   */
  delete(key: string): void {
    if (this.storageType === 'cookie') {
      this.deleteCookie(key);
    } else {
      this.deleteLocalStorage(key);
    }
  }

  /**
   * Verifica si existe una clave
   */
  exists(key: string): boolean {
    return this.get(key) !== null;
  }

  // ==================== COOKIES ====================

  private setCookie(name: string, value: string, days: number): void {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = `expires=${date.toUTCString()}`;

    // Flags de seguridad
    const secure = location.protocol === 'https:' ? 'Secure;' : '';
    const sameSite = 'SameSite=Strict';

    // Nota: No usamos HttpOnly porque necesitamos leer desde JavaScript
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;${secure}${sameSite}`;
  }

  private getCookie(name: string): string | null {
    const nameEQ = name + '=';
    const cookies = document.cookie.split(';');

    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.indexOf(nameEQ) === 0) {
        return decodeURIComponent(cookie.substring(nameEQ.length));
      }
    }
    return null;
  }

  private deleteCookie(name: string): void {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  }

  // ==================== LOCAL STORAGE ====================

  private setLocalStorage(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch (error) {
      console.error('Error al guardar en localStorage:', error);
    }
  }

  private getLocalStorage(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('Error al leer de localStorage:', error);
      return null;
    }
  }

  private deleteLocalStorage(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Error al eliminar de localStorage:', error);
    }
  }
}
